/**
 * Utility functions for the theme
 * Contains helper functions for fetch and other common operations
 * Includes performance optimizations for cart components
 */

// // Import morphing functions from the dedicated module
// export { 
//     morph
// } from '@theme/morph.js';
/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */
/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
    /** @type {Headers} */
    const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };
  
    if (type === 'javascript') {
      headers['X-Requested-With'] = 'XMLHttpRequest';
      delete headers['Content-Type'];
    }
  
    return {
      method: 'POST',
      headers: /** @type {HeadersInit} */ (headers),
      body: config.body,
    };
  }

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
    /** @type {number | undefined} */
    let timeout;
  
    /** @param {...any} args */
    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    }
  
    // Add the .cancel method:
    debounced.cancel = () => {
      clearTimeout(timeout);
    };
  
    return /** @type {T & { cancel(): void }} */ (debounced);
  }
  

/**
 * Utility function to throttle function calls
 * @param {Function} func - The function to throttle
 * @param {number} limit - The time limit in milliseconds
 * @returns {Function} The throttled function
 */
export function throttle(func, limit) {
    let inThrottle;
    return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Utility function to format price for display
 * @param {number} cents - Price in cents
 * @param {string} currency - Currency code
 * @param {boolean} includeCurrency - Whether to include currency symbol
 * @returns {string} Formatted price string
 */
export function formatPrice(cents, currency = '', includeCurrency = true) {
    const price = (cents / 100).toFixed(2);
    return includeCurrency ? `${price} ${currency}` : price;
}

/**
 * Utility function to get element by selector with fallback
 * @param {string} selector - CSS selector
 * @param {Element} parent - Parent element to search in (defaults to document)
 * @returns {Element|null} Found element or null
 */
export function getElement(selector, parent = document) {
    try {
        return parent.querySelector(selector);
    } catch (error) {
        console.warn(`Invalid selector: ${selector}`, error);
        return null;
    }
}

/**
 * TTL Cache utility class for caching data with expiration
 */
export class TTLCache {
    constructor(defaultTTL = 60000) { // 1 minute TTL for DOM elements
        this.cache = new Map();
        this.defaultTTL = defaultTTL;
    }

    /**
     * Set a value in the cache with TTL
     * @param {string} key - Cache key
     * @param {*} value - Value to cache
     * @param {number} [ttl] - Time to live in milliseconds
     */
    set(key, value, ttl = this.defaultTTL) {
        const now = Date.now();
        this.cache.set(key, {
            value,
            timestamp: now,
            ttl
        });
    }

    /**
     * Get a value from the cache if still valid
     * @param {string} key - Cache key
     * @returns {*} Cached value or null if expired
     */
    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;

        const now = Date.now();
        if (now - item.timestamp < item.ttl) {
            return item.value;
        }

        // Remove expired item
        this.cache.delete(key);
        return null;
    }

    /**
     * Check if a key exists and is valid
     * @param {string} key - Cache key
     * @returns {boolean} Whether the key exists and is valid
     */
    has(key) {
        return this.get(key) !== null;
    }

    /**
     * Clear the entire cache
     */
    clear() {
        this.cache.clear();
    }

    /**
     * Remove a specific key from cache
     * @param {string} key - Cache key
     */
    delete(key) {
        this.cache.delete(key);
    }

    /**
     * Get cache size
     * @returns {number} Number of cached items
     */
    size() {
        return this.cache.size;
    }
}

/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * Executes a callback in a separate task after the next frame.
 * Using to defer non-critical tasks until after the interaction is complete.
 * @see https://web.dev/articles/optimize-inp#yield_to_allow_rendering_work_to_occur_sooner
 * @param {() => any} callback - The callback to execute
 */
export const requestYieldCallback = (callback) => {
    requestAnimationFrame(() => {
      setTimeout(callback, 0);
    });
  };

/**
 * Event debouncer utility for managing multiple rapid events
 */
export class EventDebouncer {
    constructor() {
        this.timeouts = new Map();
    }

    /**
     * Debounce an event with a specific key
     * @param {string} key - Unique key for the event
     * @param {Function} callback - Function to execute after debounce
     * @param {number} delay - Delay in milliseconds
     */
    debounce(key, callback, delay) {
        if (this.timeouts.has(key)) {
            clearTimeout(this.timeouts.get(key));
        }
        
        const timeoutId = setTimeout(() => {
            callback();
            this.timeouts.delete(key);
        }, delay);
        
        this.timeouts.set(key, timeoutId);
    }

    /**
     * Cancel a pending debounced event
     * @param {string} key - Event key to cancel
     */
    cancel(key) {
        if (this.timeouts.has(key)) {
            clearTimeout(this.timeouts.get(key));
        }
    }

    /**
     * Clear all pending timeouts
     */
    clearAll() {
        for (const [key, timeoutId] of this.timeouts) {
            clearTimeout(this.timeouts.get(key));
        }
        this.timeouts.clear();
    }
}

// Import cart-specific functions from the dedicated module
export {
    CartCacheManager,
    parseStockErrorMessage,
    isLineIndexValid,
    setQtyInputLoadingState,
    updateAllQtyInputs,
    forceShowHideCartComponents,
    handleDynamicComponentsVisibility,
    handleEmptyCartAfterMorph
} from './cart-utilities.js';

// Initialize cart cache manager on page load
document.addEventListener('DOMContentLoaded', async () => {
    try {
        const { CartCacheManager } = await import('./cart-utilities.js');
        const cacheManager = CartCacheManager.getInstance();
        await cacheManager.initialize();
    } catch (error) {
        // Silent fail - cart cache not critical for page functionality
    }
});

/**
 * Preserve user input during morphing operations
 * @param {Element} component - Component element
 * @param {string} selector - Selector for the input element
 */
export function preserveUserInput(component, selector = 'textarea') {
    const input = component.querySelector(selector);
    if (input) {
        component._preservedValue = input.value;
    }
}

/**
 * Restore user input after morphing operations
 * @param {Element} component - Component element
 * @param {string} selector - Selector for the input element
 */
export function restoreUserInput(component, selector = 'textarea') {
    const input = component.querySelector(selector);
    if (input && component._preservedValue !== undefined) {
        input.value = component._preservedValue;
        delete component._preservedValue;
    }
}

/**
 * Supprime l'attribut "shimmer" de tous les éléments d'un conteneur donné.
 * @param {Element} [container=document.body] - Le conteneur dans lequel réinitialiser l'effet shimmer.
 */
export function clearShimmerEffects(container = document.body) {
    const elementsWithShimmer = container.querySelectorAll('[shimmer]');
    elementsWithShimmer.forEach((element) => {
        element.removeAttribute('shimmer');
    });
}

/**
 * Creates a new AbortController and cancels any previous request
 * Utility function for components that don't need the full manager class
 * @param {AbortController|null} previousController - Previous controller to abort
 * @returns {AbortController} New abort controller
 */
export function createAbortController(previousController = null) {
    if (previousController) {
        previousController.abort();
    }
    
    return new AbortController();
}


/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
    return reducedMotion.matches;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
    return typeof document.startViewTransition === 'function';
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
    current: undefined,
};

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
    'product-grid': async () => {
        const grid = document.querySelector('.l-collection');
        const productCards = /** @type {HTMLElement[]} */ ([
            ...document.querySelectorAll('.l-collection .l-grid-item'),
        ]);

        if (!grid || !productCards.length) return;

        await new Promise((resolve) =>
            requestAnimationFrame(() => {
                productCards.forEach((card, index) => {
                    const productId = card.querySelector('[data-product-id]')?.dataset.productId;
                    if (productId && index < 12) { // Animate first 12 cards
                        card.style.setProperty('view-transition-name', `product-card-${productId}`);
                    } else {
                        card.style.setProperty('content-visibility', 'hidden');
                    }
                });
                resolve(null);
            })
        );

        return () =>
            productCards.forEach((card) => {
                card.style.removeProperty('view-transition-name');
                card.style.removeProperty('content-visibility');
            });
    },
};

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
    return new Promise(async (resolve) => {
        // Check if View Transitions API is supported
        if (supportsViewTransitions() && !prefersReducedMotion()) {
            let cleanupFunctions = [];

            if (types) {
                for (const type of types) {
                    if (viewTransitionTypes[type]) {
                        const cleanupFunction = await viewTransitionTypes[type]();
                        if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
                    }
                }
            }

            const transition = document.startViewTransition(callback);

            if (!viewTransition.current) {
                viewTransition.current = transition.finished;
            }

            if (types) types.forEach((type) => transition.types.add(type));

            transition.finished.then(() => {
                viewTransition.current = undefined;
                cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
                resolve();
            });

            return;
        }

        // Fallback for browsers that don't support this API yet
        callback();
        resolve();
    });
}

export function formatMoney(value) {
    let valueWithNoSpaces = value.replace(' ', '');
    if (valueWithNoSpaces.indexOf(',') === -1) return valueWithNoSpaces;
    if (valueWithNoSpaces.indexOf(',') < valueWithNoSpaces.indexOf('.')) return valueWithNoSpaces.replace(',', '');
    if (valueWithNoSpaces.indexOf('.') < valueWithNoSpaces.indexOf(','))
        return valueWithNoSpaces.replace('.', '').replace(',', '.');
    if (valueWithNoSpaces.indexOf(',') !== -1) return valueWithNoSpaces.replace(',', '.');

    return valueWithNoSpaces;
}
/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
    const animations = Array.isArray(elements)
      ? elements.flatMap((element) => element.getAnimations(options))
      : elements.getAnimations(options);
    const animationPromises = animations.reduce((acc, animation) => {
      // Ignore ViewTimeline animations
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }
  
      return acc;
    }, /** @type {Promise<Animation>[]} */ ([]));
  
    return Promise.allSettled(animationPromises).then(callback);
  }
  export class TextComponent extends HTMLElement {
    shimmer() {
      this.setAttribute('shimmer', '');
    }
  }
  
  if (!customElements.get('text-component')) {
    customElements.define('text-component', TextComponent);
  }
  
  /**
   * Resets the shimmer attribute on all elements in the container.
   * @param {Element} [container] - The container to reset the shimmer attribute on.
   */
  export function resetShimmer(container = document.body) {
    const shimmer = container.querySelectorAll('[shimmer]');
    shimmer.forEach((item) => item.removeAttribute('shimmer'));
  }

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
    if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
        return !isPointWithinElement(event.clientX, event.clientY, element);
    }

    return !element.contains(event.target);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
    const { left, right, top, bottom } = element.getBoundingClientRect();

    return x >= left && x <= right && y >= top && y <= bottom;
}
  /**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
    if (!elements?.length) return [];
    const rootRect = root.getBoundingClientRect();
  
    return elements.filter((element) => {
      const { width, height, top, right, left, bottom } = element.getBoundingClientRect();
  
      if (ratio < 1) {
        const intersectionLeft = Math.max(rootRect.left, left);
        const intersectionRight = Math.min(rootRect.right, right);
        const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);
  
        if (axis === 'x') {
          return width > 0 && intersectionWidth / width >= ratio;
        }
  
        const intersectionTop = Math.max(rootRect.top, top);
        const intersectionBottom = Math.min(rootRect.bottom, bottom);
        const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);
  
        if (axis === 'y') {
          return height > 0 && intersectionHeight / height >= ratio;
        }
  
        const intersectionArea = intersectionWidth * intersectionHeight;
        const elementArea = width * height;
  
        // Check that at least the specified ratio of the element is visible
        return elementArea > 0 && intersectionArea / elementArea >= ratio;
      }
  
      const isWithinX = left >= rootRect.left && right <= rootRect.right;
      if (axis === 'x') {
        return isWithinX;
      }
  
      const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
      if (axis === 'y') {
        return isWithinY;
      }
  
      return isWithinX && isWithinY;
    });
  }


  /**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
    const { left, width, top, height } = element.getBoundingClientRect();
    const point = {
      x: left + width / 2,
      y: top + height / 2,
    };
  
    if (axis) return /**  @type {any} */ (point[axis]);
  
    return /**  @type {any} */ (point);
  }
  /**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
    return values.reduce(function (prev, curr) {
      return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
    });
  }

  /**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }
/**
 * A media query for large screens
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia('(min-width: 750px)');
/**
 * Prevents the default action of an event.
 * @param {Event} event - The event to prevent the default action of.
 */
export function preventDefault(event) {
    event.preventDefault();
  }
  class Scheduler {
    /** @type {Set<() => void>} */
    #queue = new Set();
    /** @type {boolean} */
    #scheduled = false;
  
    /** @param {() => void} task */
    schedule = async (task) => {
      this.#queue.add(task);
  
      if (!this.#scheduled) {
        this.#scheduled = true;
  
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;
  
        requestAnimationFrame(this.flush);
      }
    };
  
    flush = () => {
      for (const task of this.#queue) {
        task();
      }
  
      this.#queue.clear();
      this.#scheduled = false;
    };
  }
  export const scheduler = new Scheduler();

  Theme.utilities = {
    ...Theme.utilities,
    scheduler: scheduler,
  };
